import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Tooltip,
  IconButton,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalHospital as SurgeryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  MedicalServices as MedicalIcon,
  HealthAndSafety as AllergyIcon,
  AccessTime as ClockIcon
} from '@mui/icons-material';
import '../../styles/ORScheduleCalendar.css';

const ORScheduleCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Sample OR rooms
  const orRooms = [
    { id: 'ML01', name: 'ML 01', type: 'Main OR' },
    { id: 'ML02', name: 'ML 02', type: 'General Surgery' },
    { id: 'ML03', name: 'ML 03', type: 'Cardiac OR' },
    { id: 'ML04', name: 'ML 04', type: 'Orthopedic OR' },
    { id: 'ML05', name: 'ML 05', type: 'Neuro OR' },
    { id: 'ML06', name: 'ML 06', type: 'Trauma OR' }
  ];

  // Sample bookings data
  const [bookings, setBookings] = useState([
    {
      id: 1,
      roomId: 'ML01',
      patientName: 'Berry',
      procedure: 'Laparoscopic',
      startTime: '07:30',
      endTime: '09:30',
      status: 'transport',
      urgency: 'scheduled',
      allergyFlag: false,
      caseNumber: '0021',
      surgeon: 'TP',
      age: '43F',
      details: 'Berry / Welsh / MT / SC - Transport'
    },
    {
      id: 2,
      roomId: 'ML01',
      patientName: 'Berry',
      procedure: 'Lap Chole',
      startTime: '08:25',
      endTime: '10:25',
      status: 'scheduled',
      urgency: 'scheduled',
      allergyFlag: true,
      caseNumber: '0024',
      details: 'Berry / Welsh / MT / SC - Scheduled Thyroid',
      surgeon: 'CW',
      age: '67M'
    },
    {
      id: 3,
      roomId: 'ML02',
      patientName: 'Sanders',
      procedure: 'Total Shoulder',
      startTime: '08:30',
      endTime: '13:30',
      status: 'surgery-start',
      urgency: 'surgery',
      allergyFlag: false,
      caseNumber: '0564',
      surgeon: 'TP',
      age: '43F',
      details: 'Sanders / Morgan / NF / LV - Surgery Start'
    },
    {
      id: 4,
      roomId: 'ML03',
      patientName: 'Payna',
      procedure: 'Coronary Artery Bypass',
      startTime: '09:00',
      endTime: '15:00',
      status: 'anes-start',
      urgency: 'surgery',
      allergyFlag: false,
      caseNumber: '0942',
      surgeon: 'SS',
      age: '55F',
      details: 'Payna / Morgan / KG / FB - Anes Start'
    },
    {
      id: 5,
      roomId: 'ML04',
      patientName: 'Carr',
      procedure: 'Hip Replacement',
      startTime: '07:00',
      endTime: '11:00',
      status: 'surgery-start',
      urgency: 'surgery',
      allergyFlag: false,
      caseNumber: '0987',
      surgeon: 'BR',
      age: '54M',
      details: 'Carr / Hicks / BJ / AF - Surgery Start'
    },
    {
      id: 6,
      roomId: 'ML04',
      patientName: 'Carr',
      procedure: 'SLAP Repair',
      startTime: '11:30',
      endTime: '13:30',
      status: 'checked-in',
      urgency: 'scheduled',
      allergyFlag: true,
      caseNumber: '0644',
      surgeon: 'CG',
      age: '56F',
      details: 'Carr / Hicks / BJ / AF - Checked-In'
    },
    {
      id: 7,
      roomId: 'ML05',
      patientName: 'Cole',
      procedure: 'TURP',
      startTime: '09:30',
      endTime: '11:30',
      status: 'checked-in',
      urgency: 'scheduled',
      allergyFlag: false,
      caseNumber: '0644',
      surgeon: 'MT',
      age: '33M',
      details: 'Cole / Hicks / LK / HO - Checked-In',
      delayed: true
    },
    {
      id: 8,
      roomId: 'ML05',
      patientName: 'Cole',
      procedure: 'TURP',
      startTime: '12:00',
      endTime: '14:00',
      status: 'scheduled',
      urgency: 'scheduled',
      allergyFlag: false,
      caseNumber: '0856',
      surgeon: 'MT',
      age: '33M',
      details: 'Cole / Hicks / LK / HO - Scheduled',
      delayed: true
    },
    {
      id: 9,
      roomId: 'ML06',
      patientName: 'Gonzales',
      procedure: 'Pituitary',
      startTime: '08:00',
      endTime: '14:00',
      status: 'surgery-start',
      urgency: 'surgery',
      allergyFlag: false,
      caseNumber: '0644',
      surgeon: 'MT',
      age: '33M',
      details: 'Gonzales / Foster / TN / TP - Surgery Start'
    }
  ]);

  // Generate time slots for next 24 hours
  const generateTimeSlots = () => {
    const slots = [];
    const startHour = 7;
    const endHour = 19;
    
    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'surgery-start':
        return '#4CAF50'; // Green
      case 'scheduled':
        return '#2196F3'; // Blue
      case 'anes-start':
        return '#9C27B0'; // Purple
      case 'checked-in':
        return '#00BCD4'; // Cyan
      case 'transport':
        return '#FF9800'; // Orange
      case 'delayed':
        return '#F44336'; // Red
      default:
        return '#757575'; // Grey
    }
  };

  // Get status text color
  const getStatusTextColor = (status) => {
    return '#FFFFFF'; // White text for all statuses
  };

  // Calculate booking position and width
  const calculateBookingStyle = (booking) => {
    const startTimeIndex = timeSlots.findIndex(slot => slot === booking.startTime);
    const endTimeIndex = timeSlots.findIndex(slot => slot === booking.endTime);
    
    if (startTimeIndex === -1 || endTimeIndex === -1) return {};
    
    const startPercent = (startTimeIndex / timeSlots.length) * 100;
    const widthPercent = ((endTimeIndex - startTimeIndex) / timeSlots.length) * 100;
    
    return {
      left: `${startPercent}%`,
      width: `${widthPercent}%`,
      minWidth: '120px'
    };
  };

  // Format time for display
  const formatTime = (time) => {
    return time;
  };

  // Get room bookings
  const getRoomBookings = (roomId) => {
    return bookings.filter(booking => booking.roomId === roomId);
  };

  const handleBookingClick = (booking) => {
    setSelectedBooking(booking);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBooking(null);
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  return (
    <Box className="or-schedule-calendar" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50' }}>
            OR Schedule Calendar
          </Typography>
          <Chip 
            label={currentDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
            color="primary"
            variant="outlined"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigateDate(-1)}>
            <ChevronLeftIcon />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<TodayIcon />}
            onClick={goToToday}
            size="small"
          >
            Today
          </Button>
          <IconButton onClick={() => navigateDate(1)}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Status Legend */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Status Legend
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              backgroundColor: '#4CAF50', 
              borderRadius: 1 
            }} />
            <Typography variant="caption">Surgery Start</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              backgroundColor: '#2196F3', 
              borderRadius: 1 
            }} />
            <Typography variant="caption">Scheduled</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              backgroundColor: '#9C27B0', 
              borderRadius: 1 
            }} />
            <Typography variant="caption">Anesthesia Start</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              backgroundColor: '#00BCD4', 
              borderRadius: 1 
            }} />
            <Typography variant="caption">Checked In</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              backgroundColor: '#FF9800', 
              borderRadius: 1 
            }} />
            <Typography variant="caption">Transport</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AllergyIcon sx={{ fontSize: 16, color: '#FFD700' }} />
            <Typography variant="caption">Allergy Alert</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ fontSize: 16, color: '#F44336' }} />
            <Typography variant="caption">Delayed</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Calendar Grid */}
      <Paper elevation={2} sx={{ flex: 1, overflow: 'hidden', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Time Header */}
          <Box sx={{ 
            display: 'flex', 
            borderBottom: '2px solid #e0e0e0',
            backgroundColor: '#f5f5f5',
            position: 'sticky',
            top: 0,
            zIndex: 2
          }}>
            <Box sx={{ 
              width: '120px', 
              p: 2, 
              borderRight: '2px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600
            }}>
              OR Rooms
            </Box>
            <Box sx={{ 
              flex: 1, 
              display: 'flex',
              position: 'relative',
              minHeight: '60px'
            }}>
              {timeSlots.map((time, index) => (
                <Box
                  key={time}
                  sx={{
                    flex: 1,
                    borderRight: index < timeSlots.length - 1 ? '1px solid #e0e0e0' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '60px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#666'
                  }}
                >
                  {time}
                </Box>
              ))}
            </Box>
          </Box>

          {/* OR Rooms and Bookings */}
          <Box className="or-schedule-timeline" sx={{ flex: 1, overflow: 'auto' }}>
            {orRooms.map((room) => (
              <Box
                key={room.id}
                className="or-room-row"
                sx={{
                  display: 'flex',
                  borderBottom: '1px solid #e0e0e0',
                  minHeight: '80px',
                  '&:hover': {
                    backgroundColor: '#fafafa'
                  }
                }}
              >
                {/* Room Label */}
                <Box className="or-room-label" sx={{
                  width: '120px',
                  p: 2,
                  borderRight: '2px solid #e0e0e0',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  backgroundColor: '#f8f9fa'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {room.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {room.type}
                  </Typography>
                </Box>

                {/* Timeline Area */}
                <Box sx={{ 
                  flex: 1, 
                  position: 'relative',
                  minHeight: '80px'
                }}>
                  {/* Time Grid Lines */}
                  {timeSlots.map((time, index) => (
                    <Box
                      key={time}
                      sx={{
                        position: 'absolute',
                        left: `${(index / timeSlots.length) * 100}%`,
                        top: 0,
                        bottom: 0,
                        width: '1px',
                        backgroundColor: '#e0e0e0',
                        zIndex: 1
                      }}
                    />
                  ))}

                  {/* Bookings */}
                  {getRoomBookings(room.id).map((booking) => (
                    <Box
                      key={booking.id}
                      className={`or-booking-block ${booking.delayed ? 'or-booking-delayed' : ''} or-status-${booking.status}`}
                      sx={{
                        position: 'absolute',
                        top: '8px',
                        height: '64px',
                        backgroundColor: getStatusColor(booking.status),
                        color: getStatusTextColor(booking.status),
                        borderRadius: '4px',
                        cursor: 'pointer',
                        zIndex: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        px: 1,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: booking.delayed ? '2px solid #F44336' : 'none',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                          transform: 'translateY(-1px)'
                        },
                        ...calculateBookingStyle(booking)
                      }}
                      onClick={() => handleBookingClick(booking)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Typography variant="caption" sx={{ 
                          fontWeight: 600, 
                          fontSize: '0.7rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {booking.patientName}
                        </Typography>
                        {booking.allergyFlag && (
                          <AllergyIcon className="or-allergy-indicator" sx={{ fontSize: '0.7rem', color: '#FFD700' }} />
                        )}
                        {booking.delayed && (
                          <Typography variant="caption" sx={{ 
                            fontSize: '0.6rem',
                            backgroundColor: '#F44336',
                            px: 0.5,
                            borderRadius: '2px'
                          }}>
                            DELAYED
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ 
                        fontSize: '0.65rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {booking.procedure}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                          {booking.caseNumber}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                          {booking.surgeon}, {booking.age}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Booking Details Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SurgeryIcon sx={{ color: selectedBooking ? getStatusColor(selectedBooking.status) : '#666' }} />
            <Box>
              <Typography variant="h6">
                {selectedBooking?.patientName} - {selectedBooking?.procedure}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedBooking?.details}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>Patient Information</Typography>
                    <Typography variant="body2"><strong>Name:</strong> {selectedBooking.patientName}</Typography>
                    <Typography variant="body2"><strong>Age:</strong> {selectedBooking.age}</Typography>
                    <Typography variant="body2"><strong>Case Number:</strong> {selectedBooking.caseNumber}</Typography>
                    <Typography variant="body2"><strong>Surgeon:</strong> {selectedBooking.surgeon}</Typography>
                    {selectedBooking.allergyFlag && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <strong>Allergy Alert</strong>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>Procedure Details</Typography>
                    <Typography variant="body2"><strong>Procedure:</strong> {selectedBooking.procedure}</Typography>
                    <Typography variant="body2"><strong>Start Time:</strong> {selectedBooking.startTime}</Typography>
                    <Typography variant="body2"><strong>End Time:</strong> {selectedBooking.endTime}</Typography>
                    <Typography variant="body2"><strong>Room:</strong> {selectedBooking.roomId}</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip 
                        label={selectedBooking.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(selectedBooking.status),
                          color: '#fff',
                          textTransform: 'capitalize'
                        }}
                      />
                      {selectedBooking.delayed && (
                        <Chip 
                          label="DELAYED"
                          size="small"
                          color="error"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Button variant="contained" startIcon={<EditIcon />}>
            Edit Booking
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ORScheduleCalendar; 